import { apiUrl } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MonitorPlay, Globe, CalendarDays, ArrowUpDown } from 'lucide-react';
import {
  Select,
  PlatformIcon,
  MetricCell,
  Delta,
} from '@/components/primitives';
import type { MetricTone, SelectOption } from '@/components/primitives';
import { ColumnPicker, LeaderboardTable } from '@/components/organisms';
import { shortPlatform, prettyQuarter, formatUsd, formatCount, formatArpu } from '@/lib/format';
import styles from './Platforms.module.css';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PlatformEntry {
  platform: string;
  region: string;
  quarter: string;
  subscribers: number;
  arpu: number;
  revenue: number;
  gross_additions: number;
  net_additions: number;
  churns: number;
  churn_rate: number;
  retained_subs: number;
  tv_revenue: number;
  movie_revenue: number;
  subscriber_change: number;
  arpu_change: number;
  revenue_change: number;
}

interface FilterOptions {
  platforms: string[];
  regions: string[];
  quarters: string[];
}

interface Meta {
  region: string;
  quarter: string;
  count: number;
  hasMore: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

type SortField =
  | 'platform'
  | 'subscribers'
  | 'arpu'
  | 'revenue'
  | 'net_additions'
  | 'churn_rate'
  | 'subscriber_change'
  | 'gross_additions'
  | 'churns'
  | 'retained_subs'
  | 'tv_revenue'
  | 'movie_revenue'
  | 'arpu_change'
  | 'revenue_change';

type SortDir = 'asc' | 'desc';

interface ColumnDef {
  id: string;
  label: string;
  width: string;
  sortField?: SortField;
  tone?: MetricTone;
  locked?: boolean;
  defaultVisible?: boolean;
  magnitude?: (e: PlatformEntry) => number | undefined;
  help?: string;
}

const COLUMN_DEFS: ColumnDef[] = [
  {
    id: 'platform',
    label: 'Platform',
    width: '220px',
    sortField: 'platform',
    locked: true,
  },
  {
    id: 'subscribers',
    label: 'Subscribers',
    width: '150px',
    sortField: 'subscribers',
    tone: 'retained',
    defaultVisible: true,
    magnitude: (e) => e.subscribers,
    help: 'Total paying subscribers for the quarter.',
  },
  {
    id: 'arpu',
    label: 'ARPU',
    width: '120px',
    sortField: 'arpu',
    tone: 'revenue',
    defaultVisible: true,
    magnitude: (e) => e.arpu,
    help: 'Average Revenue Per User — monthly revenue divided by subscriber count.',
  },
  {
    id: 'revenue',
    label: 'Revenue',
    width: '140px',
    sortField: 'revenue',
    tone: 'revenue',
    defaultVisible: true,
    magnitude: (e) => e.revenue,
    help: 'Total quarterly revenue for the platform in the selected region.',
  },
  {
    id: 'netAdditions',
    label: 'Net Additions',
    width: '132px',
    sortField: 'net_additions',
    tone: 'acquired',
    defaultVisible: true,
    magnitude: (e) => Math.abs(e.net_additions),
    help: 'Gross additions minus churns. Negative means the platform lost subscribers.',
  },
  {
    id: 'churnRate',
    label: 'Churn Rate',
    width: '120px',
    sortField: 'churn_rate',
    tone: 'decline',
    defaultVisible: true,
    magnitude: (e) => e.churn_rate,
    help: 'Percentage of subscribers who cancelled in the quarter. Lower is better.',
  },
  {
    id: 'subscriberTrend',
    label: 'Subscriber Change',
    width: '170px',
    sortField: 'subscriber_change',
    defaultVisible: true,
    help: 'Quarter-over-quarter change in subscriber count.',
  },
  {
    id: 'grossAdditions',
    label: 'Gross Additions',
    width: '140px',
    sortField: 'gross_additions',
    tone: 'acquired',
    magnitude: (e) => e.gross_additions,
    help: 'Total new subscribers added during the quarter before churn.',
  },
  {
    id: 'churns',
    label: 'Churns',
    width: '120px',
    sortField: 'churns',
    tone: 'decline',
    magnitude: (e) => e.churns,
    help: 'Total subscribers who cancelled during the quarter.',
  },
  {
    id: 'retainedSubs',
    label: 'Retained Subscribers',
    width: '180px',
    sortField: 'retained_subs',
    tone: 'retained',
    magnitude: (e) => e.retained_subs,
    help: 'Subscribers who stayed from the previous quarter.',
  },
  {
    id: 'tvRevenue',
    label: 'TV Revenue',
    width: '132px',
    sortField: 'tv_revenue',
    tone: 'revenue',
    magnitude: (e) => e.tv_revenue,
    help: 'Revenue attributed to TV content on the platform.',
  },
  {
    id: 'movieRevenue',
    label: 'Movie Revenue',
    width: '140px',
    sortField: 'movie_revenue',
    tone: 'revenue',
    magnitude: (e) => e.movie_revenue,
    help: 'Revenue attributed to movie content on the platform.',
  },
  {
    id: 'arpuChange',
    label: 'ARPU Change',
    width: '120px',
    sortField: 'arpu_change',
    help: 'Quarter-over-quarter change in ARPU.',
  },
  {
    id: 'revenueChange',
    label: 'Revenue Change',
    width: '140px',
    sortField: 'revenue_change',
    help: 'Quarter-over-quarter change in revenue.',
  },
];

const DEFAULT_ORDER = COLUMN_DEFS.map((c) => c.id);
const DEFAULT_VISIBLE = COLUMN_DEFS.filter((c) => c.defaultVisible).map((c) => c.id);

// ─── API ───────────────────────────────────────────────────────────────────────

interface PlatformFilters {
  platform: string;
  region: string;
  quarter: string;
}

async function fetchLeaderboard(
  filters: PlatformFilters
): Promise<{ data: PlatformEntry[]; meta: Meta }> {
  const search = new URLSearchParams();
  if (filters.platform) search.set('platform', filters.platform);
  if (filters.region) search.set('region', filters.region);
  // The endpoint reads `endDate` (a quarter_end_date), NOT `quarter`. Sending
  // the wrong name silently returned the latest quarter regardless of selection.
  if (filters.quarter) search.set('endDate', filters.quarter);

  const res = await fetch(apiUrl(`/platforms/leaderboard?${search}`));
  if (!res.ok) throw new Error('Failed to fetch platform leaderboard');
  return res.json();
}

async function fetchFilters(): Promise<FilterOptions> {
  const res = await fetch(apiUrl('/platforms/filters'));
  if (!res.ok) throw new Error('Failed to fetch filters');
  return res.json();
}

// ─── Formatters ────────────────────────────────────────────────────────────────

function formatNetAdditions(value: number): string {
  const abs = Math.abs(value);
  let formatted: string;
  if (abs >= 1_000_000_000) formatted = `${(abs / 1_000_000_000).toFixed(2)}B`;
  else if (abs >= 1_000_000) formatted = `${(abs / 1_000_000).toFixed(2)}M`;
  else if (abs >= 1_000) formatted = `${(abs / 1_000).toFixed(0)}K`;
  else formatted = abs.toFixed(0);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function Platforms() {
  const [filters, setFilters] = useState<PlatformFilters>({
    platform: '',
    region: '',
    quarter: '',
  });
  const [sortField, setSortField] = useState<SortField>('subscribers');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_ORDER);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);

  const orderedColumns = useMemo(
    () =>
      columnOrder
        .map((id) => COLUMN_DEFS.find((c) => c.id === id))
        .filter((c): c is ColumnDef => Boolean(c)),
    [columnOrder]
  );

  const shownColumns = useMemo(
    () => orderedColumns.filter((c) => c.locked || visibleColumns.includes(c.id)),
    [orderedColumns, visibleColumns]
  );

  const set = (key: keyof PlatformFilters) => (value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const { data: filtersData } = useQuery({
    queryKey: ['platformFilters'],
    queryFn: fetchFilters,
    staleTime: 10 * 60 * 1000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['platformLeaderboard', filters],
    queryFn: () => fetchLeaderboard(filters),
    staleTime: 5 * 60 * 1000,
  });

  const meta = data?.meta;
  const unsortedRows = data?.data ?? [];

  /* The endpoint filters by the selected universe (platform × region × quarter)
     and returns those rows; it does NOT sort. So sorting is done here, over the
     full returned set, reacting to the Sort-by control and header clicks. */
  const rows = useMemo(() => {
    const copy = [...unsortedRows];
    copy.sort((a, b) => {
      const av = a[sortField as keyof PlatformEntry];
      const bv = b[sortField as keyof PlatformEntry];
      if (typeof av === 'string' || typeof bv === 'string') {
        const cmp = String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const cmp = (Number(av) || 0) - (Number(bv) || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [unsortedRows, sortField, sortDir]);

  /** Per-column maxima for the magnitude bar. */
  const maxima = useMemo(() => {
    const out: Record<string, number> = {};
    for (const col of COLUMN_DEFS) {
      if (!col.magnitude) continue;
      out[col.id] = rows.reduce((acc, r) => Math.max(acc, col.magnitude!(r) ?? 0), 0) || 1;
    }
    return out;
  }, [rows]);

  /** Sort-by dropdown: pick a field, default its direction sensibly. */
  const onSortSelect = (value: string) => {
    const field = value as SortField;
    setSortField(field);
    setSortDir(field === 'platform' ? 'asc' : 'desc');
  };

  /** Header-click sort from the shared table: key is the API sort field. */
  const onTableSort = ({ key, direction }: { key: string; direction: SortDir }) => {
    setSortField(key as SortField);
    setSortDir(direction);
  };

  // ─── Filter options ────────────────────────────────────────────────────────

  const opts = filtersData;

  const platformOptions: SelectOption[] = [
    { value: '', label: 'All Platforms' },
    ...(opts?.platforms?.map((p) => ({
      value: p,
      label: shortPlatform(p),
      icon: <PlatformIcon name={p} size={20} />,
    })) || []),
  ];

  const regionOptions: SelectOption[] = [
    { value: '', label: 'All Regions' },
    ...(opts?.regions?.map((r) => ({ value: r, label: r })) || []),
  ];

  // The latest quarter is represented by the "Latest (…)" default (value ''),
  // so drop it from the explicit list below to avoid listing it twice.
  const latestQuarterLabel = opts?.quarters?.[0]
    ? `Latest (${prettyQuarter(opts.quarters[0])})`
    : 'Latest';
  const quarterOptions: SelectOption[] = [
    { value: '', label: latestQuarterLabel },
    ...(opts?.quarters?.slice(1).map((q) => ({ value: q, label: prettyQuarter(q) })) || []),
  ];

  /** Sort-by options = the sortable columns, so a header click always has a
      matching dropdown entry (a missing one left the dropdown blank). */
  const SORT_OPTIONS: SelectOption[] = COLUMN_DEFS.filter((c) => c.sortField).map((c) => ({
    value: c.sortField!,
    label: c.sortField === 'platform' ? 'Platform (A–Z)' : c.label,
  }));

  // ─── Scope sentence ────────────────────────────────────────────────────────

  const total = meta?.count ?? 0;

  /* Same sentence grammar as Titles: noun first, then where, then when.
     Gold = a value the user chose; defaults stay muted. */
  const Gold = ({ children }: { children: ReactNode }) => (
    <span className={styles.scopeValue}>{children}</span>
  );
  const scopeNode = (
    <>
      {filters.platform ? (
        <Gold>{shortPlatform(filters.platform)}</Gold>
      ) : total > 0 ? (
        <>
          <strong>{total.toLocaleString()}</strong> platform{total !== 1 ? 's' : ''}
        </>
      ) : (
        'All platforms'
      )}{' '}
      {filters.region ? <>in <Gold>{filters.region}</Gold></> : 'in all regions'},{' '}
      {filters.quarter ? (
        <>in <Gold>{prettyQuarter(filters.quarter)}</Gold></>
      ) : (
        <>in the latest quarter{opts?.quarters?.[0] ? ` (${prettyQuarter(opts.quarters[0])})` : ''}</>
      )}
    </>
  );

  // ─── Render helpers ────────────────────────────────────────────────────────

  const renderCell = (col: ColumnDef, entry: PlatformEntry) => {
    const bar = (value: number | undefined) =>
      col.magnitude && value != null ? value / (maxima[col.id] ?? 1) : undefined;

    /* QoQ movement: arrow + sign + colour (DESIGN.md), same as Titles' QoQ.
       The header already says it's a change, so no "QoQ" suffix per cell. */
    const change = (value: number | null | undefined, digits: number, invert = false) =>
      value != null ? (
        <Delta value={value} digits={digits} invert={invert} />
      ) : (
        <span className={styles.noValue}>—</span>
      );

    switch (col.id) {
      case 'platform':
        return (
          <Link
            to={`/platforms/${encodeURIComponent(entry.platform)}`}
            className={styles.platformLink}
          >
            <PlatformIcon name={entry.platform} size={28} />
            <span className={styles.platformName}>{shortPlatform(entry.platform)}</span>
          </Link>
        );

      case 'subscribers':
        return (
          <MetricCell
            value={formatCount(entry.subscribers)}
            fraction={bar(entry.subscribers)}
            tone="retained"
          />
        );

      case 'arpu':
        return (
          <MetricCell
            value={formatArpu(entry.arpu)}
            fraction={bar(entry.arpu)}
            tone="revenue"
          />
        );

      case 'revenue':
        return (
          <MetricCell
            value={formatUsd(entry.revenue)}
            fraction={bar(entry.revenue)}
            tone="revenue"
          />
        );

      case 'netAdditions': {
        // Sign is in the number; the bar is size, red when it's a loss.
        const val = entry.net_additions;
        return (
          <MetricCell
            value={formatNetAdditions(val)}
            fraction={bar(Math.abs(val))}
            tone={val < 0 ? 'decline' : 'acquired'}
          />
        );
      }

      // A rate is a level, not a movement — a value + bar, not an up/down Delta.
      case 'churnRate':
        return entry.churn_rate != null ? (
          <MetricCell
            value={`${entry.churn_rate.toFixed(2)}%`}
            fraction={bar(entry.churn_rate)}
            tone="decline"
          />
        ) : (
          <span className={styles.noValue}>—</span>
        );

      case 'subscriberTrend':
        return change(entry.subscriber_change, 2);

      case 'grossAdditions':
        return (
          <MetricCell
            value={formatCount(entry.gross_additions)}
            fraction={bar(entry.gross_additions)}
            tone="acquired"
          />
        );

      case 'churns':
        return (
          <MetricCell
            value={formatCount(entry.churns)}
            fraction={bar(entry.churns)}
            tone="decline"
          />
        );

      case 'retainedSubs':
        return (
          <MetricCell
            value={formatCount(entry.retained_subs)}
            fraction={bar(entry.retained_subs)}
            tone="retained"
          />
        );

      case 'tvRevenue':
        return (
          <MetricCell
            value={formatUsd(entry.tv_revenue)}
            fraction={bar(entry.tv_revenue)}
            tone="revenue"
          />
        );

      case 'movieRevenue':
        return (
          <MetricCell
            value={formatUsd(entry.movie_revenue)}
            fraction={bar(entry.movie_revenue)}
            tone="revenue"
          />
        );

      case 'arpuChange':
        return change(entry.arpu_change, 1);

      case 'revenueChange':
        return change(entry.revenue_change, 1);

      default:
        return null;
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      {/* Define → meta, mirroring the Titles Leaderboard bar. */}
      <header className={styles.header}>
        {/* Row 1 — Define the universe */}
        <div className={styles.defineRow}>
          <Select
            value={filters.platform}
            options={platformOptions}
            onChange={set('platform')}
            searchable
            icon={
              filters.platform ? (
                <PlatformIcon name={filters.platform} size={20} />
              ) : (
                <MonitorPlay size={14} />
              )
            }
            onClear={() => set('platform')('')}
            label={'Platform'}
            highlightWhenSet
            className={styles.defineSelect}
          />
          <Select
            value={filters.region}
            options={regionOptions}
            onChange={set('region')}
            icon={<Globe size={14} />}
            onClear={() => set('region')('')}
            label={'Region'}
            highlightWhenSet
            className={styles.defineSelect}
          />
          <Select
            value={filters.quarter}
            options={quarterOptions}
            onChange={set('quarter')}
            icon={<CalendarDays size={14} />}
            onClear={() => set('quarter')('')}
            highlightWhenSet
            className={styles.defineSelect}
          />
        </div>

        {/* Row 2 — scope sentence (left) · sort + columns (right) */}
        <div className={styles.metaRow}>
          <p className={styles.subtitle}>{scopeNode}</p>

          <div className={styles.metaControls}>
            <Select
              value={sortField}
              options={SORT_OPTIONS}
              onChange={onSortSelect}
              icon={<ArrowUpDown size={14} />}
              className={styles.sortSelect}
            />
            <ColumnPicker
              columns={orderedColumns.map((c) => ({
                id: c.id,
                label: c.label,
                locked: c.locked,
              }))}
              visible={visibleColumns}
              onChange={setVisibleColumns}
              onReorder={setColumnOrder}
              onReset={() => {
                setColumnOrder(DEFAULT_ORDER);
                setVisibleColumns(DEFAULT_VISIBLE);
              }}
            />
          </div>
        </div>
      </header>

      {error && (
        <div className={styles.notice} role="alert">
          <strong>Failed to load the leaderboard.</strong>{' '}
          {(error as Error).message || 'The request did not complete.'}
        </div>
      )}

      {(isLoading || rows.length > 0) && (
        <div className={styles.tableWrapper}>
          <LeaderboardTable
            columns={shownColumns.map((col) => ({
              id: col.id,
              label: col.label,
              width: col.width,
              align: 'left' as const,
              sortKey: col.sortField,
              sortFirst: col.sortField === 'platform' ? ('asc' as const) : ('desc' as const),
              help: col.help,
              // Platform column = icon + name; the rest are metric bars.
              skeleton: col.id === 'platform' ? ('media' as const) : ('bar' as const),
              render: (entry: PlatformEntry) => renderCell(col, entry),
            }))}
            rows={rows}
            getRowKey={(entry) => entry.platform}
            sort={sortField ? { key: sortField, direction: sortDir } : undefined}
            onSortChange={onTableSort}
            loading={isLoading}
          />
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className={styles.empty}>
          <h3 className={styles.emptyTitle}>No platforms match this selection</h3>
          <p className={styles.emptyBody}>
            Try widening the region or selecting a different quarter.
          </p>
        </div>
      )}
    </div>
  );
}
